import { servicesTranslations } from "./pages/services";
import { readinessTranslations } from "./pages/readiness";
import { capitalTranslations } from "./pages/capital";
import { bookTranslations } from "./pages/book";
import { productTranslations } from "./pages/product";
import { miscTranslations } from "./pages/misc";

export type Language = "en" | "zh";

const baseTranslations = {
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.services": "Services",
    "nav.founderReadiness": "Founder Readiness",
    "nav.capitalRaising": "Capital Raising",
    "nav.founderLaws": "The Founder Laws Book",
    "nav.bookCall": "Book a Call",
    "nav.menu": "Menu",
    "nav.openMenu": "Open menu",
    "nav.language": "Language",
    "nav.signIn": "Sign in",
    "nav.governance": "Governance",
    "nav.signOut": "Sign out",


    // SEO
    "seo.home.title": "Peter Dingle — CEO Advisor, Mentor & Investor | NZ",
    "seo.home.description":
      "Personalised 1:1 advisory for NZ and Asia-Pacific founders. Become investor-ready, sharpen strategy, and scale with 20+ years of global operator experience.",

    // Hero
    "hero.title": "Founder advisory at the point where optimism meets constraint.",
    "hero.p1":
      "I work with founders navigating capital, pilots, and scale decisions, particularly when the next move matters more than more activity.",
    "hero.p2":
      "I'm based in New Zealand and active in the NZ startup ecosystem, with two decades spent across Asia Pacific and Japan helping businesses grow, partner, and scale across 18 markets.",
    "hero.tagline": "Practical thinking. Commercial clarity. Calm pressure-testing.",
    "hero.cta": "Book a 20-Minute Founder Call",
    "hero.note":
      "Experience across early-stage, corporate innovation, and venture ecosystems (NZ + Asia).",
    "hero.imageAlt": "Peter Dingle, Founder Advisor based in New Zealand",

    // Logo strip
    "logos.corporate": "Corporate & Venture Experience",
    "logos.academic": "Academic Affiliations · Studied & Taught",

    // Who I work with
    "who.title": "Who I Work With",
    "who.subtitle": "I'm most useful to founders who are:",
    "who.item1":
      "Preparing to raise, or re-raise, and want their story to hold up under investor scrutiny",
    "who.item2": "Running pilots or partnerships that aren't yet converting into real revenue",
    "who.item3":
      "Deciding whether their business is NZ-scale, offshore-scale, or something in between",
    "who.item4": "Feeling decision fatigue and wanting a thinking partner, not a cheer squad",
    "who.item5": "Past early optimism and ready for harder trade-offs",
    "who.footnote":
      "This work suits founders in New Zealand, Southeast Asia, India, Hong Kong, Singapore, and across the wider Asia-Pacific region.",

    // What I help with
    "help.title": "What I Help With",
    "help.subtitle":
      "This isn't about templates or deliverables. It's about improving decision quality at moments that compound.",
    "help.focusLabel": "Typical areas of focus include:",
    "help.item1": "Investor and capital-raising narrative",
    "help.item2": "Commercial and go-to-market clarity",
    "help.item3": "Converting pilots into paid agreements",
    "help.item4": "Founder and board-level decision framing",
    "help.item5": "NZ vs offshore scaling trade-offs",
    "help.item6": "Pressure-testing strategy before time and capital are wasted",

    // How it works
    "how.title": "How It Works",
    "how.step1.title": "Book a short call",
    "how.step1.body": "20 minutes to understand where you are and what you're navigating.",
    "how.step2.title": "Diagnostic",
    "how.step2.body": "Clarify the real problem + next moves that matter most.",
    "how.step3.title": "Retainer if there's mutual fit",
    "how.step3.body": "Ongoing advisory with monthly rhythm that fits your stage.",

    // Why this approach
    "why.title": "You Don't Need More Noise. You Need Clarity.",
    "why.p1":
      "I've spent 20+ years working inside and alongside businesses across Asia-Pacific and Japan, from early-stage ventures to large organisations, and I've seen the same patterns repeat.",
    "why.p2a": "Founders don't usually need more ideas.",
    "why.p2b": "They need fewer blind spots.",
    "why.p3":
      "My role is to help you see what's actually happening, clarify your options, and make deliberate choices, without noise or ego.",

    // Testimonials
    "testimonials.title": "Trusted by Founders & Leaders Worldwide",
    "testimonials.subtitle":
      "Real feedback from founders, executives, and entrepreneurs who have worked with Peter",
    "testimonials.footnote": "All testimonials from verified LinkedIn recommendations",
    "testimonials.role.founder": "Startup Founder",
    "testimonials.role.accelerator": "Accelerator Participant",
    "testimonials.role.mentee": "Former Mentee",
    "testimonials.role.team": "Former Team Member",
    "testimonials.role.partner": "Business Partner",
    "testimonials.russell":
      "When you are building a startup, you need to figure out what to do next. Peter's guidance kept us focused on what mattered: product and traction. His knowledge about building successful enterprises is invaluable to an early stage founder.",
    "testimonials.jacob":
      "Peter is an amazing mentor and overall knowledgeable startup advisor. He helped us refine our pitch, introduced us to useful connections, and his insight has played a part in our success to this day.",
    "testimonials.sofie":
      "Peter's insight, expertise, and tough questions sprinkled with his down to earth charm made me leave our meeting with tons of knowledge I didn't have and equally as important, extra confidence in myself and what I'm doing.",
    "testimonials.hayden":
      "Peter carried himself with utmost professionalism, always setting a high standard while fostering an environment of high performance, collaboration and growth. His mentorship provided me with foundational perspectives and advice that continue to shape how I approach challenges: to think critically, solve effectively, and scale sustainably.",
    "testimonials.zubair":
      "Peter's ability to simplify complex challenges and create a space where learning and experimentation were encouraged was remarkable. His feedback was always constructive, and he consistently pushed me to think critically and deliver with purpose.",
    "testimonials.timothy":
      "Peter is perhaps one of the most brilliant communicators I have come across. His superlative ability to capture, distill and effectively position even the most complex technologies and new business innovations to a wide spectrum of stakeholders is impressive.",

    // Products
    "products.title": "Accelerating Startups Through Proven Frameworks",
    "products.subtitle":
      "From pitch refinement to investor prep. Practical tools and guidance designed to give you the strategic edge you need, when you need it.",
    "products.tokenNote":
      "Each Advisory Token equals USD $40 and represents 20 minutes of focused learning or advisory time.",
    "products.tokensLabel": "Advisory Tokens",
    "products.tokensDesc":
      "Purchase tokens to unlock courses, workshops, and 1:1 advisory sessions",
    "products.compare": "Not sure where to start? Compare services",

    // About
    "about.work.title": "How This Shows Up In My Work",
    "about.work.intro": "I'm comfortable operating where:",
    "about.work.item1": "The market is small but ambition is global",
    "about.work.item2": "Capital is scarce and expectations are high",
    "about.work.item3": "Governance arrives earlier than founders expect",
    "about.work.item4": "The right decision is often uncomfortable, but necessary",
    "about.work.close":
      "I don't sell certainty. I help founders think clearly when certainty isn't available.",
    "about.values.title": "What I Value In Founders",
    "about.values.item1": "Intellectual honesty",
    "about.values.item2": "Willingness to confront trade-offs",
    "about.values.item3": "Openness to challenge",
    "about.values.item4": "Respect for time, theirs and mine",
    "about.values.close":
      "I'm not the right advisor for founders looking for hype, shortcuts, or validation without accountability.",
    "about.gallery.title": "From Startup Stages to Boardrooms, Globally",
    "about.gallery.subtitle":
      "Mentoring founders, running accelerators, and advising companies across markets. See the work in action.",
    "about.cta": "Book a 20-Minute Founder Call",

    // Footer
    "footer.tagline": "Based in New Zealand. Working with founders across Asia-Pacific.",
    "footer.registered": "New Zealand Registered Business",
    "footer.linkedin": "LinkedIn",
    "footer.email": "Email Peter",
    "footer.bookCall": "Book a Call",
    "footer.rights": "© 2026 Peter M Dingle. All rights reserved.",
  },
  zh: {
    // Navigation
    "nav.home": "首页",
    "nav.services": "服务",
    "nav.founderReadiness": "创始人融资准备",
    "nav.capitalRaising": "资本募集",
    "nav.founderLaws": "《创始人法则》图书",
    "nav.bookCall": "预约通话",
    "nav.menu": "菜单",
    "nav.openMenu": "打开菜单",
    "nav.language": "语言",
    "nav.signIn": "登录",
    "nav.governance": "治理后台",
    "nav.signOut": "退出登录",


    // SEO
    "seo.home.title": "Peter Dingle — CEO 顾问、导师与投资人 | 新西兰",
    "seo.home.description":
      "为新西兰及亚太地区创始人提供一对一定制顾问服务。凭借二十余年的全球实战经验，助您做好融资准备、厘清战略并实现规模化增长。",

    // Hero
    "hero.title": "当乐观遇上现实约束，正是创始人最需要顾问支持的时刻。",
    "hero.p1":
      "我与正在应对融资、试点项目与规模化决策的创始人合作——尤其是在“下一步怎么走”比“做更多事”更关键的时刻。",
    "hero.p2":
      "我常驻新西兰，活跃于本地创业生态；过去二十年深耕亚太地区与日本，曾协助企业在 18 个市场实现增长、合作与规模化。",
    "hero.tagline": "务实思考。商业洞察。冷静推演。",
    "hero.cta": "预约 20 分钟创始人通话",
    "hero.note": "拥有横跨早期创业、企业创新与风险投资生态的实战经验（新西兰及亚洲）。",
    "hero.imageAlt": "Peter Dingle，常驻新西兰的创始人顾问",

    // Logo strip
    "logos.corporate": "企业与风险投资经历",
    "logos.academic": "学术履历 · 求学与任教",

    // Who I work with
    "who.title": "我的合作对象",
    "who.subtitle": "我最能帮到这样的创始人：",
    "who.item1": "正准备首次或再次融资，希望自己的故事经得起投资人推敲",
    "who.item2": "正在推进试点项目或合作，但尚未将其转化为真实营收",
    "who.item3": "正在判断业务应立足新西兰、走向海外，还是介于两者之间",
    "who.item4": "感到决策疲劳，需要一位真正的思考伙伴，而不是只会捧场的人",
    "who.item5": "已走过早期的乐观阶段，准备直面更艰难的取舍",
    "who.footnote":
      "本服务面向位于新西兰、东南亚、印度、香港、新加坡及更广泛亚太地区的创始人。",

    // What I help with
    "help.title": "我能提供哪些帮助",
    "help.subtitle": "重点不在于模板或交付成果，而在于在关键节点上提升决策质量。",
    "help.focusLabel": "常见的重点领域包括：",
    "help.item1": "融资故事打磨与投资人沟通",
    "help.item2": "厘清商业模式与市场进入策略",
    "help.item3": "将试点项目转化为付费合同",
    "help.item4": "创始人与董事会层面的决策框架",
    "help.item5": "立足新西兰本土与走向海外之间的取舍",
    "help.item6": "在浪费时间与资金之前对战略进行压力测试",

    // How it works
    "how.title": "合作流程",
    "how.step1.title": "预约一次简短通话",
    "how.step1.body": "用 20 分钟了解您目前的处境与正在应对的挑战。",
    "how.step2.title": "诊断",
    "how.step2.body": "厘清真正的问题，以及最关键的下一步行动。",
    "how.step3.title": "若双方契合，则开启长期顾问合作",
    "how.step3.body": "以符合您所处阶段的月度节奏，提供持续的顾问支持。",

    // Why this approach
    "why.title": "您需要的不是更多噪音，而是清晰的判断。",
    "why.p1":
      "二十多年来，我在亚太地区与日本工作，既在企业内部任职，也以顾问身份与企业并肩作战——从早期创业公司到大型组织，我看到同样的模式一再上演。",
    "why.p2a": "创始人通常并不缺少点子，",
    "why.p2b": "他们需要的是更少的盲点。",
    "why.p3":
      "我的角色是帮助您看清真正在发生的事情、厘清可选路径，并在排除噪音与自我干扰的情况下，做出审慎的选择。",

    // Testimonials
    "testimonials.title": "获得全球创始人与领导者的信任",
    "testimonials.subtitle": "来自与 Peter 合作过的创始人、高管与创业者的真实反馈",
    "testimonials.footnote": "所有评价均来自经过验证的 LinkedIn 推荐",
    "testimonials.role.founder": "创业公司创始人",
    "testimonials.role.accelerator": "加速器学员",
    "testimonials.role.mentee": "曾接受其指导的学员",
    "testimonials.role.team": "前团队成员",
    "testimonials.role.partner": "业务合作伙伴",
    "testimonials.russell":
      "创业过程中，你必须不断判断下一步该做什么。Peter 的建议让我们始终聚焦于真正重要的事：产品与增长。他对如何打造成功企业的理解，对早期创始人而言极为宝贵。",
    "testimonials.jacob":
      "Peter 是一位出色的导师，也是知识渊博的创业顾问。他帮助我们打磨融资路演，引荐了有价值的人脉，他的洞见至今仍在推动我们的成功。",
    "testimonials.sofie":
      "Peter 的洞察力与专业度，加上犀利却平易近人的提问，让我在会谈结束时收获了许多此前不具备的认知；同样重要的是，我对自己和正在做的事更有信心了。",
    "testimonials.hayden":
      "Peter 始终展现出高度的专业精神，既设定高标准，又营造出高绩效、协作与成长的氛围。他的指导为我打下了基础性的视角与建议，至今仍影响着我面对挑战的方式：批判性思考、有效解决问题、可持续地扩张。",
    "testimonials.zubair":
      "Peter 能够把复杂的挑战化繁为简，并营造鼓励学习与试验的空间，这一点非常难得。他的反馈始终具有建设性，并不断推动我批判性思考、带着目标去交付成果。",
    "testimonials.timothy":
      "Peter 大概是我遇到过最出色的沟通者之一。他能够捕捉、提炼并有效呈现最复杂的技术与全新商业模式，面向各类利益相关方，这种能力令人印象深刻。",

    // Products
    "products.title": "以经过验证的框架加速创业公司成长",
    "products.subtitle":
      "从融资路演打磨到投资人对接准备，提供实用的工具与指导，在您最需要的时刻，为您带来真正的战略优势。",
    "products.tokenNote": "每个顾问点数价值 40 美元，相当于 20 分钟的专注学习或顾问时间。",
    "products.tokensLabel": "顾问点数",
    "products.tokensDesc": "购买点数即可解锁课程、工作坊与一对一顾问会谈",
    "products.compare": "不确定从哪里开始？比较各项服务",

    // About
    "about.work.title": "这些理念如何体现在我的工作中",
    "about.work.intro": "我习惯在以下情境中工作：",
    "about.work.item1": "市场规模有限，但野心是全球性的",
    "about.work.item2": "资本稀缺，而期望值很高",
    "about.work.item3": "治理要求比创始人预期来得更早",
    "about.work.item4": "正确的决定往往并不轻松，但必须去做",
    "about.work.close": "我不兜售确定性。我帮助创始人在没有确定性时依然清晰思考。",
    "about.values.title": "我看重创始人的哪些特质",
    "about.values.item1": "实事求是、坦诚面对现实",
    "about.values.item2": "愿意直面取舍",
    "about.values.item3": "乐于被质疑与挑战",
    "about.values.item4": "尊重时间——他们的，也包括我的",
    "about.values.close":
      "如果您寻求的是炒作、捷径，或不承担责任的认可，那我并不是合适的顾问。",
    "about.gallery.title": "从创业舞台到董事会会议室，足迹遍布全球",
    "about.gallery.subtitle":
      "指导创始人、运营加速器、为跨市场企业提供顾问服务。看看这些工作的实际场景。",
    "about.cta": "预约 20 分钟创始人通话",

    // Footer
    "footer.tagline": "常驻新西兰，服务于整个亚太地区的创始人。",
    "footer.registered": "新西兰注册企业",
    "footer.linkedin": "领英 LinkedIn",
    "footer.email": "发邮件给 Peter",
    "footer.bookCall": "预约通话",
    "footer.rights": "© 2026 Peter M Dingle. 版权所有。",
  },
} as const;

export const translations = {
  en: {
    ...baseTranslations.en,
    ...servicesTranslations.en,
    ...readinessTranslations.en,
    ...capitalTranslations.en,
    ...bookTranslations.en,
    ...productTranslations.en,
    ...miscTranslations.en,
  },
  zh: {
    ...baseTranslations.zh,
    ...servicesTranslations.zh,
    ...readinessTranslations.zh,
    ...capitalTranslations.zh,
    ...bookTranslations.zh,
    ...productTranslations.zh,
    ...miscTranslations.zh,
  },
} as const;

export type TranslationKey = keyof (typeof translations)["en"];
